import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const RegionTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [regions, setRegions] = useState([]);
    const [newName, setNewName] = useState("");
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState("");

    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const perms = user.adminRole?.permissions?.shippingAreas || {};
    const canCreate = isSuperAdmin || perms.create;
    const canEdit = isSuperAdmin || perms.edit;
    const canDelete = isSuperAdmin || perms.delete;

    const fetchRegions = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/regions');
            setRegions(data.regions);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load regions", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRegions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addHandler = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        setAdding(true);
        try {
            await axios.post('/api/v1/admin/region/new', { name: newName.trim() });
            enqueueSnackbar("Region added", { variant: "success" });
            setNewName("");
            fetchRegions();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to add region", { variant: "error" });
        } finally {
            setAdding(false);
        }
    };

    const startEdit = (region) => {
        setEditingId(region._id);
        setEditValue(region.name);
    };

    const saveEdit = async (id) => {
        if (!editValue.trim()) return;
        try {
            await axios.put(`/api/v1/admin/region/${id}`, { name: editValue.trim() });
            enqueueSnackbar("Region updated", { variant: "success" });
            setEditingId(null);
            fetchRegions();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to update region", { variant: "error" });
        }
    };

    const deleteHandler = async (id) => {
        if (!window.confirm("Delete this region? This can't be undone.")) return;
        try {
            await axios.delete(`/api/v1/admin/region/${id}`);
            enqueueSnackbar("Region deleted", { variant: "success" });
            setRegions((prev) => prev.filter((r) => r._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete region", { variant: "error" });
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title="Admin: Regions | 1313 Mart" />

            <div className="flex justify-between items-center mb-4">
                <h1 className="text-lg font-medium uppercase">Regions</h1>
                <Link to="/admin/shipping-areas" className="text-sm font-medium text-primary-blue hover:underline">
                    &larr; Back to Shipping Areas
                </Link>
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full p-4 sm:p-6 flex flex-col gap-4">

                {canCreate && (
                    <form onSubmit={addHandler} className="flex gap-3 items-start">
                        <TextField
                            label="New Region Name"
                            size="small"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            sx={{ minWidth: 260 }}
                        />
                        <button
                            type="submit"
                            disabled={adding}
                            className="bg-primary-orange px-5 py-2 text-white font-medium rounded shadow hover:shadow-lg disabled:opacity-60"
                        >
                            Add Region
                        </button>
                    </form>
                )}

                <div className="flex flex-col divide-y">
                    {regions.map((region) => (
                        <div key={region._id} className="flex items-center justify-between py-3">
                            {editingId === region._id ? (
                                <TextField
                                    size="small"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    autoFocus
                                    sx={{ minWidth: 260 }}
                                />
                            ) : (
                                <span className="text-sm font-medium text-gray-700">{region.name}</span>
                            )}

                            <div className="flex items-center gap-1">
                                {editingId === region._id ? (
                                    <>
                                        <IconButton size="small" onClick={() => saveEdit(region._id)}>
                                            <CheckIcon sx={{ fontSize: "20px" }} color="success" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => setEditingId(null)}>
                                            <CloseIcon sx={{ fontSize: "20px" }} color="error" />
                                        </IconButton>
                                    </>
                                ) : (
                                    <>
                                        {canEdit && (
                                            <IconButton size="small" onClick={() => startEdit(region)}>
                                                <EditIcon sx={{ fontSize: "18px" }} color="primary" />
                                            </IconButton>
                                        )}
                                        {canDelete && (
                                            <IconButton size="small" onClick={() => deleteHandler(region._id)}>
                                                <DeleteIcon sx={{ fontSize: "18px" }} color="error" />
                                            </IconButton>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    ))}

                    {regions.length === 0 && (
                        <p className="text-sm text-gray-400 py-3">No regions yet — add one above.</p>
                    )}
                </div>
            </div>
        </>
    );
};

export default RegionTable;